import { useEffect, useRef } from "react";
import { StarIcon } from "@heroicons/react/outline";
import { StarIcon as StarIconSolid } from "@heroicons/react/solid";
import { isCheckActive } from "../../features/cases/caseFilters";

const isFav = (c) => c.fav === 1 || c.fav === true;

// Flex columns never shrink below this; the table scrolls instead.
const FLEX_MIN = 64;

const StatusCell = ({ singleCase, pending }) => {
  const state = singleCase?.check_status?.state;
  if (pending || isCheckActive(singleCase)) {
    return (
      <span
        className="ds-status ds-status--active"
        title={
          state === "retrying"
            ? "Η ανάκτηση στοιχείων θα επαναληφθεί αυτόματα."
            : "Γίνεται ανάκτηση στοιχείων από το solon.gov.gr."
        }
      >
        <span className="ds-spinner" aria-hidden="true" />
        <span className="sr-only">Σε έλεγχο</span>
      </span>
    );
  }
  if (state === "failed") {
    const error = singleCase?.check_status?.last_error;
    return (
      <span
        className="ds-status ds-status--failed"
        title={`Η ανάκτηση στοιχείων απέτυχε${error ? `: ${error}` : "."}`}
      >
        !
      </span>
    );
  }
  if (!singleCase?.last_checked && !singleCase?.check_status) {
    return (
      <span className="ds-status ds-status--never" title="Δεν έχει ελεγχθεί ακόμη">
        ⧗
      </span>
    );
  }
  return null;
};

// Status cell for case types without a server-side check queue: a spinner
// while a check is pending, ⧗ until the first check has run.
export const SimpleStatusCell = ({ singleCase, pending }) => {
  if (pending) {
    return (
      <span className="ds-status ds-status--active" title="Γίνεται έλεγχος…">
        <span className="ds-spinner" aria-hidden="true" />
        <span className="sr-only">Σε έλεγχο</span>
      </span>
    );
  }
  if (!singleCase?.last_checked) {
    return (
      <span className="ds-status ds-status--never" title="Δεν έχει ελεγχθεί ακόμη">
        ⧗
      </span>
    );
  }
  return null;
};

const defaultRowLabel = (c) => `${c.gak}/${c.year}`;
const defaultRenderStatus = (c, pending) => <StatusCell singleCase={c} pending={pending} />;

const SortIndicator = ({ dir }) =>
  dir ? (
    <span className="ds-sort-indicator" aria-hidden="true">
      {dir === "asc" ? "▲" : "▼"}
    </span>
  ) : null;

const CasesTable = ({
  rows,
  columns,
  sort,
  onSort,
  selected,
  onToggleSelect,
  onSelectPage,
  focusedId,
  openId,
  onRowClick,
  onToggleFav,
  pendingCheckIds,
  getRowLabel = defaultRowLabel,
  showFav = true,
  // undefined: the Αστικά check status; null: no status column.
  renderStatus = defaultRenderStatus,
  ariaLabel = "Υποθέσεις",
}) => {
  const bodyRef = useRef(null);

  useEffect(() => {
    if (focusedId === null || focusedId === undefined || !bodyRef.current) return;
    const row = bodyRef.current.querySelector(`[data-row-id="${focusedId}"]`);
    row?.scrollIntoView({ block: "nearest" });
  }, [focusedId]);

  const pageIds = rows.map((r) => r.id);
  const selectedOnPage = pageIds.filter((id) => selected.has(id)).length;
  const allOnPage = pageIds.length > 0 && selectedOnPage === pageIds.length;

  const showStatus = renderStatus !== null;
  const fixedCols = [36, showFav && 34, showStatus && 28].filter(Boolean);

  // Fixed columns keep their width; flex columns split the rest but never
  // shrink below a readable minimum (the table scrolls instead).
  const minTableWidth =
    fixedCols.reduce((sum, w) => sum + w, 0) +
    columns.reduce((sum, col) => sum + (col.flex ? Math.min(col.width, FLEX_MIN) : col.width), 0);

  const ariaSort = (id) =>
    sort?.id === id ? (sort.dir === "asc" ? "ascending" : "descending") : "none";

  return (
    <table
      className="ds-table"
      aria-label={ariaLabel}
      aria-rowcount={rows.length}
      style={{ minWidth: minTableWidth }}
    >
      <colgroup>
        {fixedCols.map((width, i) => (
          <col key={`fixed-${i}`} style={{ width }} />
        ))}
        {columns.map((col) => (
          <col key={col.id} style={col.flex ? undefined : { width: col.width }} />
        ))}
      </colgroup>
      <thead>
        <tr>
          <th scope="col" className="ds-th-fixed">
            <input
              type="checkbox"
              aria-label="Επιλογή όλων στη σελίδα"
              checked={allOnPage}
              ref={(el) => {
                if (el) el.indeterminate = selectedOnPage > 0 && !allOnPage;
              }}
              onChange={(e) => onSelectPage(e.target.checked)}
            />
          </th>
          {showFav && (
            <th scope="col" className="ds-th-fixed" aria-sort={ariaSort("fav")}>
              <button
                type="button"
                className="ds-th-btn"
                onClick={() => onSort("fav")}
                title="Ταξινόμηση κατά σημαντικά"
              >
                ★<SortIndicator dir={sort?.id === "fav" ? sort.dir : null} />
              </button>
            </th>
          )}
          {showStatus && (
            <th scope="col" className="ds-th-fixed">
              <span className="sr-only">Κατάσταση</span>
            </th>
          )}
          {columns.map((col) => (
            <th key={col.id} scope="col" aria-sort={ariaSort(col.id)}>
              <button
                type="button"
                className="ds-th-btn"
                onClick={() => onSort(col.id)}
              >
                {col.label}
                <SortIndicator dir={sort?.id === col.id ? sort.dir : null} />
              </button>
            </th>
          ))}
        </tr>
      </thead>
      <tbody ref={bodyRef}>
        {rows.map((c) => {
          const checked = selected.has(c.id);
          const classes = [
            "ds-row",
            c.id === focusedId ? "ds-row--focused" : "",
            c.id === openId ? "ds-row--open" : "",
            checked ? "ds-row--checked" : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <tr
              key={c.id}
              data-row-id={c.id}
              className={classes}
              aria-selected={c.id === openId}
              onClick={(e) => onRowClick(c.id, e)}
            >
              <td
                className="ds-td-fixed"
                onClick={(e) => {
                  e.stopPropagation();
                  if (e.target.tagName !== "INPUT") {
                    onToggleSelect(c.id, { range: e.shiftKey });
                  }
                }}
              >
                <input
                  type="checkbox"
                  aria-label={`Επιλογή ${getRowLabel(c)}`}
                  checked={checked}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleSelect(c.id, { range: e.shiftKey });
                  }}
                  onChange={() => {}}
                />
              </td>
              {showFav && (
                <td className="ds-td-fixed">
                  <button
                    type="button"
                    className={`ds-star ${isFav(c) ? "is-fav" : ""}`}
                    aria-pressed={isFav(c)}
                    aria-label={isFav(c) ? "Αφαίρεση από τα σημαντικά" : "Προσθήκη στα σημαντικά"}
                    title={isFav(c) ? "Αφαίρεση από τα σημαντικά" : "Προσθήκη στα σημαντικά"}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleFav(c.id);
                    }}
                  >
                    {isFav(c) ? <StarIconSolid /> : <StarIcon />}
                  </button>
                </td>
              )}
              {showStatus && (
                <td className="ds-td-fixed">
                  {renderStatus(c, pendingCheckIds?.has(String(c.id)))}
                </td>
              )}
              {columns.map((col) => (
                <td key={col.id}>{col.render(c)}</td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default CasesTable;
